<template>
    <div class="pivot-field-selector">
        <div class="selector-header">
            <span class="selector-title">{{ title }}</span>
            <a-button 
                v-if="allowMultiple && selectedFields.length > 0" 
                type="link" 
                size="small"
                @click="clearAll"
            >
                Tümünü Temizle
            </a-button>
        </div>

        <div class="field-filter">
            <a-input-search
                v-model="searchQuery"
                placeholder="Alan ara..."
                size="small"
                allowClear
            />
            <a-select
                v-model="typeFilter"
                size="small"
                style="width: 150px; margin-left: 8px"
                placeholder="Tür filtrele"
                allowClear
            >
                <a-select-option value="all">Tümü</a-select-option>
                <a-select-option value="number">Sayısal</a-select-option>
                <a-select-option value="string">Metin</a-select-option>
                <a-select-option value="date">Tarih</a-select-option>
            </a-select>
        </div>

        <div class="selected-fields" v-if="allowMultiple && selectedFields.length > 0">
            <div class="selected-title">Seçili Alanlar ({{ selectedFields.length }})</div>
            <draggable
                v-model="selectedFieldsModel"
                :options="{ animation: 200, handle: '.drag-handle' }"
                @end="onOrderChanged"
            >
                <div 
                    v-for="(fieldId, index) in selectedFieldsModel" 
                    :key="fieldId"
                    class="selected-field-item"
                >
                    <i class="material-icons drag-handle">drag_indicator</i>
                    <span class="field-name">{{ getFieldName(fieldId) }}</span>
                    <span class="field-type">{{ getFieldType(fieldId) }}</span>
                    <i 
                        class="material-icons remove-icon" 
                        @click="removeField(fieldId)"
                    >
                        close
                    </i>
                </div>
            </draggable>
        </div>

        <div class="available-fields">
            <div class="available-title">Mevcut Alanlar</div>
            <div class="field-list">
                <div 
                    v-for="field in filteredFields" 
                    :key="field._id"
                    class="field-item"
                    :class="{ 'is-selected': isFieldSelected(field._id) }"
                    @click="toggleField(field._id)"
                >
                    <i class="material-icons field-icon">{{ getFieldIcon(field) }}</i>
                    <span class="field-name">{{ field.name }}</span>
                    <span class="field-type-badge">{{ getFieldTypeLabel(field) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";

export default Vue.extend({
    name: "PivotFieldSelector",
    components: {
        draggable
    },
    props: {
        title: {
            type: String,
            default: "Alan Seçimi"
        },
        fields: {
            type: Array,
            required: true
        },
        value: {
            type: Array,
            default: () => []
        },
        allowMultiple: {
            type: Boolean,
            default: true
        },
        fieldTypeFilter: {
            type: String,
            default: null // 'numeric', 'all', etc.
        }
    },
    data() {
        return {
            searchQuery: "",
            typeFilter: "all",
            selectedFieldsModel: this.value ? [...this.value] : []
        };
    },
    computed: {
        selectedFields() {
            return this.selectedFieldsModel;
        },
        filteredFields() {
            let fields = this.fields;

            // Pivot'da kullanılamaz field'ları filtrele
            const excludedFieldTypes = [
                'json', 
                'event', 
                'file', 
                'image', 
                'video', 
                'audio', 
                'automation-button', 
                'linked-record', 
                'lookup'
            ];
            fields = fields.filter(f => !excludedFieldTypes.includes(f.fieldType));

            // Type filter
            if (this.fieldTypeFilter === 'numeric') {
                fields = fields.filter(f => 
                    ['number', 'currency', 'percent'].includes(f.fieldType) || 
                    f.valueType === 'number'
                );
            } else if (this.typeFilter !== 'all') {
                fields = fields.filter(f => {
                    if (this.typeFilter === 'number') {
                        return ['number', 'currency', 'percent'].includes(f.fieldType) || f.valueType === 'number';
                    } else if (this.typeFilter === 'string') {
                        return f.valueType === 'string';
                    } else if (this.typeFilter === 'date') {
                        return f.fieldType === 'datetime';
                    }
                    return true;
                });
            }

            // Search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                fields = fields.filter(f => f.name.toLowerCase().includes(query));
            }

            return fields;
        }
    },
    methods: {
        getFieldName(fieldId) {
            const field = this.fields.find(f => f._id === fieldId);
            return field ? field.name : fieldId;
        },
        getFieldType(fieldId) {
            const field = this.fields.find(f => f._id === fieldId);
            return field ? this.getFieldTypeLabel(field) : '';
        },
        getFieldTypeLabel(field) {
            if (field.fieldType === 'datetime') return 'Tarih';
            if (['number', 'currency', 'percent'].includes(field.fieldType)) return 'Sayı';
            if (field.valueType === 'number') return 'Sayı';
            if (field.valueType === 'string') return 'Metin';
            return field.fieldType || 'Diğer';
        },
        getFieldIcon(field) {
            if (field.fieldType === 'datetime') return 'calendar_today';
            if (['number', 'currency', 'percent'].includes(field.fieldType)) return 'tag';
            if (field.valueType === 'number') return 'tag';
            return 'text_fields';
        },
        isFieldSelected(fieldId) {
            return this.selectedFieldsModel.includes(fieldId);
        },
        toggleField(fieldId) {
            if (this.allowMultiple) {
                const index = this.selectedFieldsModel.indexOf(fieldId);
                if (index > -1) {
                    this.selectedFieldsModel.splice(index, 1);
                } else {
                    this.selectedFieldsModel.push(fieldId);
                }
            } else {
                this.selectedFieldsModel = [fieldId];
            }
            this.emitChange();
        },
        removeField(fieldId) {
            const index = this.selectedFieldsModel.indexOf(fieldId);
            if (index > -1) {
                this.selectedFieldsModel.splice(index, 1);
                this.emitChange();
            }
        },
        clearAll() {
            this.selectedFieldsModel = [];
            this.emitChange();
        },
        onOrderChanged() {
            this.emitChange();
        },
        emitChange() {
            this.$emit('input', [...this.selectedFieldsModel]);
            this.$emit('change', [...this.selectedFieldsModel]);
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.selectedFieldsModel)) {
                    this.selectedFieldsModel = [...newVal];
                }
            },
            deep: true
        }
    }
});
</script>

<style scoped lang="scss">
.pivot-field-selector {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .selector-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .selector-title {
            font-weight: 600;
            font-size: 14px;
            color: #333;
        }
    }

    .field-filter {
        display: flex;
        gap: 8px;
    }

    .selected-fields {
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 12px;
        background-color: #fafafa;

        .selected-title {
            font-size: 13px;
            font-weight: 600;
            color: #666;
            margin-bottom: 8px;
        }

        .selected-field-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px;
            background: #fff;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            margin-bottom: 6px;
            cursor: move;

            &:last-child {
                margin-bottom: 0;
            }

            .drag-handle {
                font-size: 18px;
                color: #999;
                cursor: grab;

                &:active {
                    cursor: grabbing;
                }
            }

            .field-name {
                flex: 1;
                font-size: 13px;
                color: #333;
            }

            .field-type {
                font-size: 11px;
                color: #999;
                background: #f0f0f0;
                padding: 2px 6px;
                border-radius: 3px;
            }

            .remove-icon {
                font-size: 18px;
                color: #999;
                cursor: pointer;

                &:hover {
                    color: #ff4d4f;
                }
            }
        }
    }

    .available-fields {
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 12px;

        .available-title {
            font-size: 13px;
            font-weight: 600;
            color: #666;
            margin-bottom: 8px;
        }

        .field-list {
            max-height: 300px;
            overflow-y: auto;

            .field-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px;
                border-radius: 4px;
                cursor: pointer;
                transition: background-color 0.2s;

                &:hover {
                    background-color: #f5f5f5;
                }

                &.is-selected {
                    background-color: #e6f7ff;
                    border: 1px solid #91d5ff;
                }

                .field-icon {
                    font-size: 18px;
                    color: #999;
                }

                .field-name {
                    flex: 1;
                    font-size: 13px;
                    color: #333;
                }

                .field-type-badge {
                    font-size: 11px;
                    color: #666;
                    background: #f0f0f0;
                    padding: 2px 6px;
                    border-radius: 3px;
                }
            }
        }
    }
}
</style>

